import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MyelinaMark } from "@/components/brand/MyelinaMark";
import { BuildInfo } from "@/components/BuildInfo";
import { scrollToSection } from "@/lib/scrollToSection";


const Footer = forwardRef<HTMLElement>((_, ref) => {
  const year = new Date().getFullYear();
  return (
    <footer
      ref={ref}
      aria-label="Site footer"
      className="relative bg-primary text-primary-foreground overflow-hidden"
    >
      {/* Top hairline that fades at the edges */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary-foreground/15 to-transparent" />

      {/* Glow */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-primary-foreground/[0.04] blur-[140px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-10 lg:px-16 pt-20 pb-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-20">
          {/* Brand column */}
          <div className="lg:col-span-6 space-y-6">
            <Link to="/" className="inline-flex items-center gap-3 group rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/70 focus-visible:ring-offset-2 focus-visible:ring-offset-primary" aria-label="Myelina Health, home">
              <MyelinaMark className="w-7 h-7 text-primary-foreground" aria-hidden="true" />
              <span className="font-display text-xl tracking-tight">
                Myelina Health<span className="text-[10px] align-top ml-0.5 text-primary-foreground/60">™</span>
              </span>
            </Link>
            <p className="lead-italic text-primary-foreground/80 max-w-md" style={{ color: "inherit" }}>
              See your energy coming.
            </p>
            <p className="text-sm text-primary-foreground/60 leading-relaxed max-w-md">
              A quiet companion for women living with MS. Notice your pattern,
              plan the day that fits it. A wellness tool, never a medical device.
            </p>
            <div className="pt-2">
              <Link to="/signup" aria-label="Request access, open the invite signup">
                <Button
                  size="lg"
                  tabIndex={-1}
                  className="group bg-primary-foreground text-primary hover:bg-primary-foreground/90 min-h-11 focus-visible:ring-primary-foreground focus-visible:ring-offset-primary"
                >
                  Request access
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Links columns */}
          <div className="lg:col-span-6 grid grid-cols-2 md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <h3 className="text-[10px] tracking-[0.28em] uppercase text-primary-foreground/50">Product</h3>
              <ul className="space-y-1 -mx-2">
                {[
                  { id: "features", label: "Features" },
                  { id: "how-it-works", label: "How it works" },
                  { id: "principles", label: "Principles" },
                  { id: "faq", label: "FAQ" },
                ].map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => void scrollToSection(l.id)}
                      aria-label={`Scroll to ${l.label} section`}
                      className="flex items-center min-h-11 px-2 w-full text-left text-sm text-primary-foreground/75 hover:text-primary-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/70 focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-[10px] tracking-[0.28em] uppercase text-primary-foreground/50">Company</h3>
              <ul className="space-y-1 -mx-2">
                {[
                  { to: "/blog", label: "Journal" },
                  { to: "/learn", label: "Learn library" },
                  { to: "/resources", label: "Resources" },
                  { to: "/state-of-ms-energy", label: "State of MS Energy" },
                  { to: "/energy-gap", label: "Energy Gap report" },
                  { to: "/demo", label: "Try the demo" },
                ].map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} aria-label={`${l.label} page`} className="flex items-center min-h-11 px-2 text-sm text-primary-foreground/75 hover:text-primary-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/70 focus-visible:ring-offset-2 focus-visible:ring-offset-primary">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4 col-span-2 md:col-span-1">
              <h3 className="text-[10px] tracking-[0.28em] uppercase text-primary-foreground/70">Legal &amp; trust</h3>
              <ul className="space-y-1 -mx-2 grid grid-cols-2 md:grid-cols-1 gap-x-2">
                {[
                  { to: "/privacy", label: "Privacy" },
                  { to: "/support", label: "Support" },
                  { to: "/terms", label: "Terms" },
                  { to: "/security", label: "Security" },
                  { to: "/trust", label: "Trust" },
                  { to: "/model-card", label: "Model card" },
                  { to: "/waitlist", label: "Waitlist" },
                  { to: "/advisors", label: "Advisory board" },
                  { to: "/investors", label: "Investors" },
                  { to: "/trademarks", label: "Trademarks" },
                  { to: "/status", label: "Status" },
                  { to: "/changelog", label: "Changelog" },
                  // Press kit temporarily unpublished pending trademark clearance.
                  { to: "/login", label: "Log in" },
                ].map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} aria-label={`${l.label} page`} className="flex items-center min-h-[52px] py-2.5 px-3 text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/70 focus-visible:ring-offset-2 focus-visible:ring-offset-primary">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="border-y border-primary-foreground/15 py-6 mb-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px] text-primary-foreground/80">
          <div className="flex items-center gap-2"><span aria-hidden="true">🔒</span> We never sell your data</div>
          <div className="flex items-center gap-2"><span aria-hidden="true">🛡️</span> Encrypted, quietly</div>
          <div className="flex items-center gap-2"><span aria-hidden="true">💜</span> Built by someone who lives it</div>
          <div className="flex items-center gap-2"><span aria-hidden="true">⚖️</span> A wellness tool, not medical advice</div>
        </div>

        {/* Oversized wordmark, the exhale */}
        <div className="relative pt-4 pb-2 overflow-hidden">
          <div
            aria-hidden="true"
            className="font-display leading-[0.85] tracking-[-0.04em] text-primary-foreground/40 select-none whitespace-nowrap"
            style={{ fontSize: "clamp(4rem, 14vw, 11rem)" }}
          >
            Myelina Health<span className="align-top text-[0.4em] ml-2">™</span>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-primary-foreground/15 to-transparent my-8" />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-xs text-primary-foreground/50">© {year} Myelina Health. All rights reserved.</p>
            <BuildInfo />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/clinicians/portal"
              aria-label="Clinician preview page"
              className="inline-flex items-center min-h-11 px-2 text-[11px] text-primary-foreground/50 hover:text-primary-foreground/80 tracking-[0.24em] uppercase transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/70 focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            >
              Clinician preview
            </Link>
            <p className="text-[10px] text-primary-foreground/60 tracking-[0.28em] uppercase px-2">
              Made with women who live with MS
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
